/* eslint-disable max-len */
import React, {
  useEffect,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';
import { TextField, Button, Checkbox, InputAdornment } from '@one-thd/sui-atomic-components';
import { Mobile, Checkmark } from '@one-thd/sui-icons';
import { useAisleBayTextMeDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { AisleBayTextMeFooter } from './AisleBayTextMeFooter';
import {
  cleanNumber,
  formatPhoneNumber,
  isValidPhoneNumber,
  publish
} from '../helper';

const TEMPLATES = {
  templateForInvalidPhone: (
    <span className="sui-text-danger sui-text-xs">Please enter a valid 10-digit phone number.</span>
  ),
  templateForSubscribeSuccess: (
    <div className="sui-flex sui-flex-col">
      <div className="sui-flex sui-flex-col sui-items-center sui-justify-items-center sui-text-success sui-my-auto sui-mr-1">
        <Checkmark color="inherit" size="small" />
      </div>
      <div className="sui-font-bold sui-mb-2 sui-text-center sui-text-lg">{'We\'ll text you shortly'}</div>
      <span className="sui-text-xs ">
        {'You\'ll receive a text with product\'s store location from 20841 and a text from 65624 to finish signing up for The Home Depot Text Program (and get your'}
        <span className="sui-font-bold"> $5 off coupon</span>)
      </span>
    </div>
  ),
  temlateForTextSuccess: (
    <div className="sui-flex sui-flex-row">
      <div className="sui-text-success sui-my-auto sui-mr-2">
        <Checkmark color="inherit" size="small" />
      </div>
      <div className="sui-text-xs">
        {'A text message with the product\'s in-store location will arrive shortly. See you soon!'}
      </div>
    </div>
  ),
  templateForTextFailure: (
    <span className="sui-text-xs sui-text-danger">
      {'We\'re sorry. We\'re experiencing technical difficulties. Please try again in a few minutes.'}
    </span>
  )
};

const AisleBayTextMe = forwardRef((props, ref) => {
  const { itemId, storeId, aisleBayText } = props;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [status, setStatus] = useState('');
  const [phoneNumberFieldStatus, setPhoneNumberFieldStatus] = useState({ status: '', message: '' });

  const resetState = () => {
    setPhoneNumber('');
    setIsSubscribed(false);
    setStatus('');
  };

  useImperativeHandle(ref, () => ({ resetState }));

  const [makeRequest, response] = useAisleBayTextMeDeprecated({
    itemId,
    storeId,
    aisleBayText,
    phoneNumber: phoneNumber.replace(/-/g, ''),
    isSavingsSubscribed: isSubscribed
  }, 'aisleBayTextMe');
  const result = response?.data?.aisleBayTextMe?.result;

  useEffect(() => {
    if (result === 'opt_in_required' || result === 'success') {
      setStatus('SUCCESS');
      publish('success', { type: 'success' });
    }
    if (result === '' || result === 'error') {
      setStatus('ERROR');
      publish('error', {
        type: 'error',
        data: {
          code: 500,
          message: 'Internal Server Error'
        }
      });
    }
  }, [result, setStatus]);

  const handleTextme = useCallback((ev) => {
    ev.preventDefault();
    if (!isValidPhoneNumber({ phoneNumber: cleanNumber(phoneNumber) })) {
      setStatus('INVALID_PHONE_NUMBER');
    } else {
      makeRequest({
        variables: {
          itemId,
          storeId,
          aisleBayText,
          phoneNumber: cleanNumber(phoneNumber),
          isSavingsSubscribed: isSubscribed
        }
      });
    }
  }, [setStatus, phoneNumber, makeRequest, isSubscribed]);

  const handleChange = useCallback((ev) => {
    const { value } = ev.target;
    let formatted;
    if (ev.nativeEvent.inputType === 'deleteContentBackward') {
      const cleanedValue = cleanNumber(phoneNumber);
      const lastNumRemoved = cleanedValue.slice(0, cleanedValue.length - 1);
      formatted = formatPhoneNumber(lastNumRemoved);
    } else {
      const cleanedValue = cleanNumber(value);
      formatted = formatPhoneNumber(cleanedValue);
    }

    if (formatted.length <= 12) {
      setPhoneNumber(formatted);
    }
  }, [setPhoneNumber]);

  return (
    <>
      <div className="sui-py-4">
        {['SUCCESS', 'ERROR'].indexOf(status) === -1 && (
          <form>
            <div className="sui-flex sui-flex-col sui-pb-4">
              <div className="sui-flex sui-flex-row sui-justify-between sui-align-center">
                <TextField
                  id="phoneNumber"
                  name="phoneNumber"
                  label="Mobile Phone:"
                  placeholder="___-___-____"
                  type="tel"
                  value={phoneNumber}
                  status={phoneNumberFieldStatus.status}
                  statusMessage={phoneNumberFieldStatus.message}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mobile color="subtle" />
                      </InputAdornment>
                    )
                  }}
                  autoComplete="off"
                />
                <div className="sui-mt-auto sui-ml-4 sui-grow">
                  <Button onClick={handleTextme} variant="secondary" fullWidth>
                    Text Me
                  </Button>
                </div>
              </div>
              {status === 'INVALID_PHONE_NUMBER'
                && TEMPLATES.templateForInvalidPhone}
            </div>
            <div className="sui-text-xs sui-flex sui-flex-row">
              <div className="sui--mt-2">
                <Checkbox
                  onChange={() => setIsSubscribed(!isSubscribed)}
                  checked={isSubscribed}
                  inputAttributes={{ 'aria-label': 'controlled' }}
                  id="subscriptionAgreement"
                  name="subscriptionAgreement"
                />
              </div>
              <label
                htmlFor="subscriptionAgreement"
              >
                Yes, I want <span className="sui-font-bold">$5 off my next in store purchase</span>
                {' and receive $100s more in savings right to my phone by joining the Home Depot Text Program*'}
              </label>
            </div>
          </form>
        )}
        {status === 'SUCCESS' && isSubscribed
            && TEMPLATES.templateForSubscribeSuccess}
        {status === 'SUCCESS' && !isSubscribed
            && TEMPLATES.temlateForTextSuccess}
        {status === 'ERROR'
            && TEMPLATES.templateForTextFailure}
      </div>
      <AisleBayTextMeFooter />
    </>
  );
});

AisleBayTextMe.propTypes = {
  aisleBayText: PropTypes.string,
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string
};

AisleBayTextMe.defaultProps = {
  aisleBayText: '',
  storeId: null
};

export default AisleBayTextMe;
